import React from 'react';
import {
  MenuItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  IconButton,
  Typography,
  Box,
  alpha,
  useTheme
} from '@mui/material';
import {
  Favorite as LikeIcon,
  Comment as CommentIcon,
  PersonAdd as FollowIcon,
  AlternateEmail as MentionIcon,
  Info as SystemIcon,
  NotificationsNone as DefaultIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { Notification } from '../../types/notification';

interface NotificationItemProps {
  notification: Notification;
  onNotificationClick: (notification: Notification) => void;
  onDelete: (id: string) => void;
  isLoading: boolean;
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'like':
      return <LikeIcon color="error" />;
    case 'comment':
      return <CommentIcon color="primary" />;
    case 'follow':
      return <FollowIcon color="success" />;
    case 'mention':
      return <MentionIcon color="info" />;
    case 'system':
      return <SystemIcon color="warning" />;
    default:
      return <DefaultIcon />;
  }
};

const NotificationItem = React.memo<NotificationItemProps>(({
  notification,
  onNotificationClick,
  onDelete,
  isLoading
}) => {
  const theme = useTheme();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  return (
    <MenuItem
      onClick={() => onNotificationClick(notification)}
      disabled={isLoading}
      role="article"
      aria-label={`${notification.read ? 'Read' : 'Unread'} notification: ${notification.message}`}
      sx={{
        py: 1.5,
        px: 2,
        backgroundColor: notification.read 
          ? 'inherit' 
          : alpha(theme.palette.primary.main, 0.1),
        '&:hover': {
          backgroundColor: notification.read 
            ? alpha(theme.palette.action.hover, 0.1)
            : alpha(theme.palette.primary.main, 0.2)
        },
        transition: theme.transitions.create(['background-color']),
        position: 'relative',
        alignItems: 'flex-start',
        width: '100%',
        overflow: 'hidden'
      }}
    >
      <ListItemAvatar>
        <Avatar 
          sx={{ 
            bgcolor: notification.read 
              ? alpha(theme.palette.action.active, 0.1)
              : alpha(theme.palette.primary.main, 0.1),
            color: notification.read
              ? theme.palette.text.secondary
              : theme.palette.primary.main
          }}
        >
          {getNotificationIcon(notification.type)}
        </Avatar>
      </ListItemAvatar>
      
      <ListItemText
        sx={{ 
          flex: 1, 
          minWidth: 0,
          pr: 5 // Extra space for delete button
        }}
        primary={
          <Typography
            variant="body2"
            color={notification.read ? 'text.primary' : 'primary'}
            sx={{ 
              fontWeight: notification.read ? 'normal' : 500,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.5,
              fontSize: '0.875rem'
            }}
          >
            {notification.message}
          </Typography>
        }
        secondary={
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            mt: 0.5 
          }}>
            <Typography 
              variant="caption" 
              color="text.secondary"
            >
              {formatDistanceToNow(new Date(notification.createdAt), { 
                addSuffix: true 
              })}
            </Typography>
            {!notification.read && (
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  fontWeight: 'bold',
                  textTransform: 'uppercase'
                }}
              >
                New
              </Typography>
            )}
          </Box>
        }
      />

      <IconButton
        size="small"
        onClick={handleDelete}
        disabled={isLoading}
        aria-label={`Delete notification: ${notification.message}`}
        sx={{
          position: 'absolute',
          right: theme.spacing(1),
          opacity: 0,
          transition: theme.transitions.create(['opacity']),
          '.MuiMenuItem-root:hover &': {
            opacity: 1
          }
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </MenuItem>
  );
});

NotificationItem.displayName = 'NotificationItem';

function areEqual(prevProps: NotificationItemProps, nextProps: NotificationItemProps) {
  return (
    prevProps.notification.id === nextProps.notification.id &&
    prevProps.notification.read === nextProps.notification.read &&
    prevProps.isLoading === nextProps.isLoading
  );
}

export default React.memo(NotificationItem, areEqual);

